import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Scale, Handshake, Heart } from "lucide-react";
import SevaFundSection from "../components/SevaFundSection";
import ImpactReportsSection from "../components/ImpactReportsSection";

// Proposed stakeholder chart — editable as we scale
const CHART = [
  {
    group: "Devotional Core",
    rows: [
      { who: "Temple Donation", fee: 5, who_gets: "Pancham Dham (95%)", example: "$100 donation → $95 reaches the altar" },
      { who: "Seva Booking (annadaan, abhishekam)", fee: 5, who_gets: "Pancham Dham (95%)", example: "$108 seva → $103 to the pandits & kitchen" },
      { who: "Daily Blessing Club ($108/mo)", fee: 10, who_gets: "Pancham Dham (90%)", example: "$108 club dues → $97 to the temple ops" },
      { who: "Gift a Seva (in friend's name)", fee: 5, who_gets: "Pancham Dham (95%)", example: "Same as a direct seva" },
    ],
  },
  {
    group: "Pilgrimage & Travel",
    rows: [
      { who: "Cambodia Yatra Package", fee: 10, who_gets: "Travel desk & local hosts (90%)", example: "$1,299 trip → $1,169 to lodging, meals, guides" },
      { who: "Custom Yatra Enquiry (future)", fee: 10, who_gets: "Travel desk + partner agents", example: "Negotiated per group" },
    ],
  },
  {
    group: "Teachers Marketplace",
    rows: [
      { who: "Guru / Priest Booking", fee: 20, who_gets: "Guru (80%)", example: "$75 reading → $60 to the guru" },
      { who: "Astrologer Reading", fee: 20, who_gets: "Astrologer (80%)", example: "$50 session → $40 to the astrologer" },
      { who: "Scholar Lecture / Discourse", fee: 20, who_gets: "Scholar (80%)", example: "$30 lecture → $24 to the scholar" },
      { who: "Bhajan Singer / Devotional Music", fee: 20, who_gets: "Artist (80%)", example: "$40 bhajan → $32 to the singer" },
      { who: "Wedding Ceremony", fee: 15, who_gets: "Pandit team (85%)", example: "$500 wedding → $425 to the officiant" },
      { who: "Household Puja (griha pravesh, naamkaran)", fee: 15, who_gets: "Pandit (85%)", example: "$80 puja → $68 to the pandit" },
      { who: "Vastu / Tarot consult", fee: 20, who_gets: "Consultant (80%)", example: "$60 consult → $48 to the consultant" },
      { who: "Live Group Satsang (one-to-many)", fee: 20, who_gets: "Teacher (80%)", example: "100 × $5 ticket → $400 to the teacher" },
    ],
  },
  {
    group: "Commerce & Suppliers",
    rows: [
      { who: "Prasad Box Subscription ($29/mo)", fee: 15, who_gets: "Supplier + fulfillment (85%)", example: "$29 box → $24.65 to supplier (incl. shipping)" },
      { who: "Rudraksha / Mala Marketplace (proposed)", fee: 15, who_gets: "Verified seller (85%)", example: "$50 rudraksha → $42.50 to seller" },
      { who: "Ghee Diya / Incense Vendors (proposed)", fee: 15, who_gets: "Vendor (85%)", example: "$12 diya pack → $10.20 to vendor" },
      { who: "Spiritual Books / Media (proposed)", fee: 20, who_gets: "Author / Publisher (80%)", example: "$15 book → $12 to author" },
    ],
  },
  {
    group: "Advertising & Partnerships",
    rows: [
      { who: "Sponsored Banner on Home (proposed)", fee: 100, who_gets: "Platform", example: "$299/mo listing — full retention" },
      { who: "Business Directory listing (proposed)", fee: 100, who_gets: "Platform", example: "$49/mo listing" },
      { who: "Email Newsletter Shoutout (proposed)", fee: 100, who_gets: "Platform", example: "$149/send" },
    ],
  },
];

export default function Transparency() {
  const [live, setLive] = useState(null);
  useEffect(() => { api.get("/platform-fees").then((r) => setLive(r.data.rates_percent)).catch(() => {}); }, []);

  return (
    <div data-testid="transparency-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div className="overline">॥ पारदर्शिता ॥</div>
      <h1 className="font-serif text-5xl lg:text-6xl text-[#4A0E17] mt-2 leading-tight">How we share, honestly</h1>
      <p className="text-[#63534E] mt-5 text-lg max-w-3xl leading-relaxed">
        Every rupee you offer here reaches the right pair of hands. We retain a small <strong className="text-[#E65100]">success fee</strong> only when a transaction completes — enough to keep the platform running, servers alive, and devotional content free for every seeker.
      </p>

      <div className="mt-10 grid md:grid-cols-3 gap-5">
        <div className="bg-[#FFFDF9] sacred-border rounded-2xl p-6">
          <Scale className="w-8 h-8 text-[#E65100]" />
          <div className="font-serif text-xl text-[#4A0E17] mt-4">No hidden fees</div>
          <p className="text-sm text-[#63534E] mt-2">The fee percentage is shown before every checkout. What you see is what we take.</p>
        </div>
        <div className="bg-[#FFFDF9] sacred-border rounded-2xl p-6">
          <Handshake className="w-8 h-8 text-[#E65100]" />
          <div className="font-serif text-xl text-[#4A0E17] mt-4">Fair to providers</div>
          <p className="text-sm text-[#63534E] mt-2">Teachers earn 80% · suppliers 85% · the temple gets 90–95% of every donation.</p>
        </div>
        <div className="bg-[#FFFDF9] sacred-border rounded-2xl p-6">
          <Heart className="w-8 h-8 text-[#E65100]" />
          <div className="font-serif text-xl text-[#4A0E17] mt-4">Negotiable at scale</div>
          <p className="text-sm text-[#63534E] mt-2">Verified providers crossing $1,000/month get preferential rates. Reach out any time.</p>
        </div>
      </div>

      <div className="mt-14 space-y-10">
        {CHART.map((section) => (
          <div key={section.group} data-testid={`section-${section.group.replace(/\s/g, '-').toLowerCase()}`}>
            <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
              <h2 className="font-serif text-3xl text-[#4A0E17]">{section.group}</h2>
            </div>
            <div className="bg-[#FFFDF9] sacred-border rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#4A0E17] text-[#F3E5AB]">
                  <tr>
                    <th className="text-left px-5 py-3">Transaction</th>
                    <th className="text-center px-5 py-3 w-32">Platform fee</th>
                    <th className="text-left px-5 py-3">Who gets the rest</th>
                    <th className="text-left px-5 py-3 hidden md:table-cell">Worked example</th>
                  </tr>
                </thead>
                <tbody>
                  {section.rows.map((r, i) => (
                    <tr key={i} className="border-t border-[#D4AF37]/20">
                      <td className="px-5 py-4 font-semibold text-[#4A0E17]">{r.who}</td>
                      <td className="px-5 py-4 text-center">
                        <span className={`inline-flex items-center justify-center min-w-[60px] px-3 py-1 rounded-full text-sm font-mono font-semibold ${r.fee <= 10 ? "bg-green-100 text-green-800" : r.fee <= 20 ? "bg-amber-100 text-amber-800" : "bg-red-50 text-red-800"}`}>
                          {r.fee}%
                        </span>
                      </td>
                      <td className="px-5 py-4 text-[#1F1412]">{r.who_gets}</td>
                      <td className="px-5 py-4 text-xs text-[#63534E] font-mono hidden md:table-cell">{r.example}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>

      {live && (
        <div className="mt-14 bg-[#F3E5AB]/40 border border-[#D4AF37]/40 rounded-2xl p-6">
          <div className="overline">Currently Live</div>
          <p className="text-[#1F1412] mt-2 text-sm">The following rates are enforced today in the backend. Items marked <em>proposed</em> above will launch as we onboard those stakeholders.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {Object.entries(live).map(([k, v]) => (
              <span key={k} className="bg-white border border-[#D4AF37]/40 rounded-full px-3 py-1 text-xs font-mono text-[#4A0E17]">
                {k} · <strong className="text-[#E65100]">{v}%</strong>
              </span>
            ))}
          </div>
        </div>
      )}

      <SevaFundSection />
      <ImpactReportsSection />

      <div className="mt-14 bg-[#4A0E17] text-[#FAF6F0] rounded-3xl p-10 text-center relative overflow-hidden">
        <div className="absolute -top-10 right-0 font-devanagari text-[260px] leading-none text-[#D4AF37] opacity-10 pointer-events-none">ॐ</div>
        <h2 className="font-serif text-3xl relative">Have questions? Need a custom rate?</h2>
        <p className="text-[#FAF6F0]/80 mt-3 relative">Email us or submit the yatra enquiry form — we respond within 24 hours.</p>
        <div className="mt-6 flex flex-wrap gap-3 justify-center relative">
          <Link to="/teachers/apply" data-testid="apply-teacher" className="btn-saffron">Apply as a teacher</Link>
          <Link to="/pilgrimage#enquiry" data-testid="partner-cta" className="btn-ghost-gold !text-[#F3E5AB] !border-[#D4AF37] hover:!bg-[#D4AF37]/15">Partner with us</Link>
        </div>
      </div>
    </div>
  );
}
